import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_SOURCE_VOLUME, sourceGain, type Channel } from "@gooner/shared";

/**
 * The screen's radio: one channel at a time, looping forever, surviving the panel that
 * controls it.
 *
 * THE ELEMENTS ARE DETACHED, not JSX, for two reasons that both bite. React has no
 * `volume` attribute, so a level set in markup is a level that never arrives — the same
 * trap RegionPlayer documents for its crossfade slots. And this hook is called by the
 * SCREEN, not the panel: the panel unmounts every time the dropdown closes, and audio that
 * lived in its tree would stop the moment somebody looked away from it.
 *
 * IT LOOPS BECAUSE THE FILE LOOPS. `audioUrl` is the body and carries `loop`; `introUrl`,
 * when a channel has one, plays once and hands over on `ended`. Nothing schedules anything,
 * which is the point: the alternative was decoding the whole track through Web Audio for
 * sample-accurate loop points, and a twelve-minute channel is 267 MB of PCM resident in the
 * browser for as long as it plays. The body is preloaded while the intro runs, so the one
 * handoff per session is covered.
 *
 * THE GRAPH EXISTS FOR ONE REASON: iOS. `HTMLMediaElement.volume` is read-only there (see
 * RegionPlayer, which has the same note), so a fader that only wrote `el.volume` would work
 * on every desktop and silently do nothing on an iPad. Routing the element through a
 * GainNode fixes that — and note it is `createMediaElementSource`, a live tap on a
 * streaming element, NOT `decodeAudioData`, so it costs no buffer. If the context cannot be
 * built at all we fall back to `el.volume`, which is wrong only in the place that was
 * already broken.
 *
 * IT MOUNTS PAUSED, always. Nothing on this screen may make noise without a gesture, and a
 * remembered "was playing" would be a promise the autoplay policy refuses to keep.
 *
 * THE FADES belong to Play 1's bed (see DisplayApp's bed effect): `play`/`pause` take
 * `{ fade: true }` to ramp a SECOND gain node rather than cut — out in 400ms so a panel's
 * first words are not fought for, in over a second so the return is a swell. The ramp
 * rides its own node because the level node is rewritten by the volume effect on every
 * master drag, which would stomp a ramp folded into it. A faded pause stays `playing`
 * until the ramp lands, because the transport must not read "paused" over a bed still
 * audibly leaving. Where the graph could not be built there are no ramps — the fallback
 * cuts, which is wrong only where volume was already broken.
 */
const FADE_IN_MS = 1000;
const FADE_OUT_MS = 400;
export function useRadio({
  channels,
  master,
}: {
  channels: readonly Channel[];
  /** The master fader, multiplying the channel's own level — see sourceGain. */
  master: number;
}) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  /** The channel's OWN fader, 0..1 — the panel's small slider under the transport. Starts
   *  at the HALF default, the same DEFAULT_SOURCE_VOLUME an unstored region level reads
   *  as; ephemeral like the master, because which room is listening is not a fact about
   *  the view. */
  const [level, setLevel] = useState(DEFAULT_SOURCE_VOLUME);
  const current = channels[index] ?? null;

  const introRef = useRef<HTMLAudioElement | null>(null);
  const bodyRef = useRef<HTMLAudioElement | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  /** The fade multiplier, in series with the level's own node — see the fades note above. */
  const fadeRef = useRef<GainNode | null>(null);
  /** The pause armed for a fade-out's end — cancelled by any newer transport press. */
  const fadeStop = useRef<number | null>(null);
  /** Elements already tapped: createMediaElementSource throws if called twice on one. */
  const tapped = useRef(new WeakSet<HTMLAudioElement>());

  const route = useCallback((el: HTMLAudioElement) => {
    const Ctx = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    try {
      ctxRef.current ??= new Ctx();
      gainRef.current ??= ctxRef.current.createGain();
      if (!fadeRef.current) {
        fadeRef.current = ctxRef.current.createGain();
        fadeRef.current.connect(gainRef.current);
      }
      gainRef.current.connect(ctxRef.current.destination);
      if (tapped.current.has(el)) return;
      ctxRef.current.createMediaElementSource(el).connect(fadeRef.current);
      tapped.current.add(el);
    } catch {
      // No graph: el.volume is the fallback, and the volume effect writes both anyway.
      // (Fades are graph-only — the fallback cuts, wrong only where volume was already
      // broken.)
    }
  }, []);

  /** Abandon any ramp mid-flight and hold the fade where it is. Every transport press
   *  passes through here first, so a newer intent always beats a scheduled one — the
   *  armed pause of a fade-out included, or a bed that flapped would strand a timer
   *  that silences the wrong future. */
  const cancelFade = useCallback(() => {
    if (fadeStop.current !== null) {
      window.clearTimeout(fadeStop.current);
      fadeStop.current = null;
    }
    const fade = fadeRef.current;
    const ctx = ctxRef.current;
    if (fade && ctx) {
      fade.gain.cancelScheduledValues(ctx.currentTime);
      fade.gain.setValueAtTime(fade.gain.value, ctx.currentTime);
    }
  }, []);

  // Build the pair for the tuned channel, and tear down the previous one. Teardown is
  // `src = ""` + load(), which is the ritual that actually releases a stream — pausing
  // alone leaves it buffering.
  useEffect(() => {
    if (!current) return;
    const body = new Audio(current.audioUrl);
    body.loop = true;
    /*
     * NOTHING IS FETCHED UNTIL SOMEBODY PRESSES PLAY.
     *
     * `preload="auto"` here cost twelve megabytes on every screen load, radio or not —
     * measured: both files requested 0.8 s after mount, before any gesture. A wall that
     * never touches the radio must not pay for it, and a media box serving a room full of
     * screens pays for it once per screen. `play()` overrides this, so the only thing it
     * delays is a download nobody asked for.
     */
    body.preload = "none";
    bodyRef.current = body;

    let intro: HTMLAudioElement | null = null;
    if (current.introUrl) {
      intro = new Audio(current.introUrl);
      intro.preload = "none";
      // The one handoff. `loop` is already true on the body, so it takes over forever.
      intro.addEventListener("ended", () => {
        void body.play().catch(() => undefined);
      });
      introRef.current = intro;
    } else {
      introRef.current = null;
    }

    return () => {
      // A fade-out armed against the OLD channel must not land on the new one.
      cancelFade();
      for (const el of [intro, body]) {
        if (!el) continue;
        el.pause();
        el.src = "";
        el.load();
      }
      introRef.current = null;
      bodyRef.current = null;
    };
  }, [current, cancelFade]);

  // One gain law, the same one the tiles use: the master multiplies the source's own
  // level — and the channel's own level is a real fader now, not a literal 1.
  useEffect(() => {
    const gain = sourceGain(master, level);
    if (gainRef.current) gainRef.current.gain.value = gain;
    for (const el of [introRef.current, bodyRef.current]) {
      if (el) el.volume = gainRef.current ? 1 : gain;
    }
  }, [master, level, current]);

  const stop = useCallback(() => {
    introRef.current?.pause();
    bodyRef.current?.pause();
    setPlaying(false);
  }, []);

  /** `fade: true` ramps to silence first and pauses at the ramp's end — `playing` stays
   *  true until the sound is actually gone. Without the option (or without a graph, or
   *  with nothing audibly playing) it cuts, which is what a mute press should do. */
  const pause = useCallback(
    (opts?: { fade?: boolean }) => {
      cancelFade();
      const fade = fadeRef.current;
      const ctx = ctxRef.current;
      const active = [introRef.current, bodyRef.current].some((el) => el && !el.paused);
      if (opts?.fade && fade && ctx && active) {
        fade.gain.linearRampToValueAtTime(0, ctx.currentTime + FADE_OUT_MS / 1000);
        fadeStop.current = window.setTimeout(() => {
          fadeStop.current = null;
          stop();
        }, FADE_OUT_MS);
      } else {
        stop();
      }
    },
    [cancelFade, stop],
  );

  const play = useCallback(
    (opts?: { fade?: boolean }) => {
      const intro = introRef.current;
      const body = bodyRef.current;
      if (!body) return;
      for (const el of [intro, body]) if (el) route(el);
      void ctxRef.current?.resume().catch(() => undefined);
      cancelFade();
      const fade = fadeRef.current;
      const ctx = ctxRef.current;
      if (fade && ctx) {
        if (opts?.fade) {
          // A fresh start swells from silence; a rescue mid-fade-out continues from
          // wherever the ramp had got to, because cancelFade held it there.
          const active = [intro, body].some((el) => el && !el.paused);
          if (!active) fade.gain.setValueAtTime(0, ctx.currentTime);
          fade.gain.linearRampToValueAtTime(1, ctx.currentTime + FADE_IN_MS / 1000);
        } else {
          fade.gain.setValueAtTime(1, ctx.currentTime);
        }
      }
      // Resume whichever is mid-track; otherwise start at the top of the intro.
      // The body starts buffering the moment the intro does, so the one handoff per session
      // lands on a primed element rather than an empty one. Only from here — this is the
      // gesture, and before it nothing had been fetched.
      body.preload = "auto";
      const started = intro && intro.currentTime > 0 && !intro.ended ? intro : body.currentTime > 0 ? body : intro ?? body;
      started
        .play()
        .then(() => setPlaying(true))
        // Refused, or the file is missing. Stay honest rather than showing a playing
        // transport over silence — and never retry muted: a muted video is still most of a
        // video, a silent radio channel is a lie with a progress bar.
        .catch(() => setPlaying(false));
    },
    [route, cancelFade],
  );

  const toggle = useCallback(() => (playing ? pause() : play()), [playing, pause, play]);

  const step = useCallback(
    (dir: 1 | -1) => {
      if (channels.length === 0) return;
      setIndex((i) => (i + dir + channels.length) % channels.length);
    },
    [channels.length],
  );

  // A bfcached tab with live audio keeps broadcasting on some mobile browsers. Note this
  // is `pagehide` and deliberately NOT `visibilitychange`: a wall TV reports hidden the
  // moment its screensaver kicks in, and the radio is exactly what should keep playing.
  useEffect(() => {
    const stop = () => pause();
    window.addEventListener("pagehide", stop);
    return () => window.removeEventListener("pagehide", stop);
  }, [pause]);

  return { current, playing, play, pause, toggle, step, level, setLevel };
}
